import { Query, UseFetchStatus, useFetch, useLocales } from "hooks";
import { useState } from "react";

import { Subscription as PushSubscription } from "./usePush";

export interface Subscription {
  id: number;
  station_key: string;
  station_name: string;
  last_measured_at: string;
  condition: string;
  threshold: number;
  last_value: number;
  unit_label: string;
  last_notification: string;
}
interface ListSubscriptionsReturn {
  subscriber_id: number;
  subscriptions: readonly Subscription[];
}
interface CreateSubscriptionParams {
  station_key: string;
  condition: string;
  threshold: number | "";
}

interface UseSubscriptionsParams {
  enabled: boolean;
  pushSubscription?: PushSubscription;
  stationKey?: string;
}
interface UseSubscriptionsReturn {
  subscriberId?: number;
  subscriptions?: Subscription[];
  status: UseFetchStatus;
  refresh: () => void;
  destroy: (subscription: Subscription) => Promise<void>;
  subscriptionIdsBeingDeleted: number[];
  create: (values: CreateSubscriptionParams) => Promise<void>;
}

export function useSubscriptions({
  enabled,
  pushSubscription,
  stationKey,
}: UseSubscriptionsParams): UseSubscriptionsReturn {
  const { locale } = useLocales();

  const listSubscriptionsPath = `/${locale}/subscriptions/list`;
  const deleteSubscriptionPath = (subscription: Subscription) =>
    `/${locale}/subscriptions/${subscription.id}`;
  const createSubscriptionPath = `/${locale}/subscriptions`;

  const query: Query = { endpoint: pushSubscription?.endpoint || "" };
  if (stationKey) query["station_key"] = stationKey;

  const { data, status, refetch } = useFetch<ListSubscriptionsReturn | null>({
    url: listSubscriptionsPath,
    query,
    options: { enabled, method: "POST" },
  });
  const { subscriber_id: subscriberId, subscriptions } = data || {};

  const refresh = () => {
    refetch(listSubscriptionsPath, query);
  };

  // Delete
  const [subscriptionIdsBeingDeleted, setSubscriptionIdsBeingDeleted] =
    useState<number[]>([]);
  const destroy = (subscription: Subscription) => {
    setSubscriptionIdsBeingDeleted((subscriptionIdsBeingDeleted) => [
      ...subscriptionIdsBeingDeleted,
      subscription.id,
    ]);
    return fetch(deleteSubscriptionPath(subscription), {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(query),
    })
      .catch((error) => {
        setSubscriptionIdsBeingDeleted((subscriptionIdsBeingDeleted) =>
          subscriptionIdsBeingDeleted.filter((id) => id !== subscription.id)
        );
        throw error;
      })
      .then((res) => {
        if (res.status != 200) throw new Error();
      });
  };

  // Create
  const create = (values: CreateSubscriptionParams) => {
    if (!pushSubscription) return Promise.reject();

    return fetch(createSubscriptionPath, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...pushSubscription, ...values }),
    }).then((res) => {
      if (res.status != 200) throw new Error();
    });
  };

  // Sort
  const getSortKey = (subscription: Subscription) =>
    subscription.station_name + subscription.condition;
  const sortedSubscriptions = subscriptions
    ?.slice()
    ?.sort((a, b) => getSortKey(a).localeCompare(getSortKey(b), locale));

  return {
    subscriberId,
    subscriptions: sortedSubscriptions,
    status,
    refresh,
    destroy,
    subscriptionIdsBeingDeleted,
    create,
  };
}
